import {
    GroupType,
    IAnalyticsModelResponse,
    ITreeItemResponse,
    SetPropertyChild,
} from '@kcftech/prometheustypescriptapiclient';
import { AnalyticsModelAssignmentFormData } from 'components/analytics/AnalyticsAssignmentForm';
import { Subtree } from 'pages/workbench/side_panel/workbench_tree/useLoadSubtree';
import { Link } from 'react-router-dom';
import { getGroupTypeUserFriendlyName } from 'utils/group';
import { getIntegrationsLink } from 'utils/RouteHelper';
import { TreeItemType } from 'utils/tree';

export const buildModelProperties = (
    model: IAnalyticsModelResponse,
    parameters: AnalyticsModelAssignmentFormData['parameters']
) =>
    model.Properties.map<SetPropertyChild>(
        (property) =>
            new SetPropertyChild({
                PropertyId: property.PropertyId,
                Value: parameters[`${property.PropertyId}`]?.trim() ?? '',
            })
    ).filter((propertyChild) => propertyChild.Value.length > 0);

export type TitleAndValidTypes = Readonly<{
    title: string;
    validTypes: GroupType[];
}>;

const topLevelGroupOptions = {
    title: 'Add a New Group, Assembly, or Asset',
    validTypes: [GroupType.Group, GroupType.Assembly, GroupType.Asset],
};

export const getTreeItemTitleAndOptions = (parentItem: Subtree): TitleAndValidTypes => {
    switch (parentItem.TreeItemType) {
        case GroupType.System:
            return topLevelGroupOptions;
        case GroupType.Group:
            // if the group has a parent group, then no more groups may be added to the hierarchy
            if (parentItem.parent?.TreeItemType === GroupType.Group) {
                return {
                    title: 'Add a New Assembly or Asset',
                    validTypes: [GroupType.Assembly, GroupType.Asset],
                };
            }
            return topLevelGroupOptions;
        case GroupType.Assembly:
            return {
                title: 'Add a New Asset',
                validTypes: [GroupType.Asset],
            };
        case GroupType.Asset:
            return {
                title: 'Add a New Monitoring Point',
                validTypes: [GroupType.MonitoringPoint],
            };

        case GroupType.MonitoringPoint:
            return {
                title: 'Add a New Indicator Group',
                validTypes: [GroupType.IndicatorGroup],
            };

        default:
            throw new Error(`Invalid parent type: ${parentItem.TreeItemType}`);
    }
};

export const getTreeItemDeleteActionText = (
    type: TreeItemType | GroupType,
    isDynamicSystem: boolean
) => {
    switch (type) {
        case 'System':
            if (isDynamicSystem) {
                return 'The groups and indicators below the dynamic system will be retained by their static system(s).';
            } else {
                return 'All groups, assemblies, assets, monitoring points, and indicators will also be deleted. The data will no longer be available.';
            }
        case 'Group':
            return 'All assets, monitoring points, and indicators below this group will also be deleted. The data will no longer be available.';
        case 'Assembly':
            return 'All assets, monitoring points, and indicators below this group will also be deleted. The data will no longer be available.';
        case 'Asset':
            return 'All monitoring points and indicators below this asset will also be deleted. The data will no longer be available.';
        case 'MonitoringPoint':
            return 'All indicators below this monitoring point will also be deleted. The data will no longer be available.';
        case 'IndicatorGroup':
            return 'All indicators below this indicator group will also be deleted. The data will no longer be available.';
        default:
            throw new Error(`Type not supported: ${type}`);
    }
};

export const ValidModelAggregateGroupType = [
    GroupType.System,
    GroupType.Group,
    GroupType.Assembly,
    GroupType.Asset,
    GroupType.MonitoringPoint,
];

export const buildTreeChildrenData = (children: ITreeItemResponse[]) =>
    children
        .filter((child) => ValidModelAggregateGroupType.includes(child.TreeItemType))
        .map((data) => ({
            id: data.Id,
            data,
        }));

export const getIssueCountText = (
    issueCount: number | undefined,
    type: TreeItemType | GroupType,
    isDynamicSystem: boolean = false
) => {
    if (!issueCount) {
        return undefined;
    }

    const entityName = getGroupTypeUserFriendlyName(type, isDynamicSystem).toLowerCase();

    if (issueCount === 1) {
        return `1 issue exists in this ${entityName} that will lose its fault location if you do not select a new group for it`;
    }
    return `${issueCount} issues exist in this ${entityName} that will lose their fault location if you do not select a new group for them`;
};

export const getIntegrationText = (
    locationId: string,
    type: TreeItemType | GroupType,
    isDynamicSystem: boolean = false
) => {
    const entityName = getGroupTypeUserFriendlyName(type, isDynamicSystem).toLowerCase();
    return (
        <>
            <Link to={getIntegrationsLink(locationId)}>Integrations</Link> with any
            external applications using this {entityName} will be affected
        </>
    );
};
